import {HierarchyNodeType} from '@domain/shared'
import {DataContentWrapper} from '@hconnect/common/containers'
import {useExpandedGroups} from '@hconnect/common/hooks/useExpandGroups'
import {
  PageContainer,
  ExpandCollapseButton,
  buttonOnBlueSx,
  mergeSxs
} from '@hconnect/uikit/src/lib2'
import React, {FC, Fragment, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {useEquipmentStatus} from '../../shared/hooks'
import {CollapseLocationItem} from '../components/CollapseLocationItem'
import {DateTimeRangePicker} from '../components/DateTimeRangePicker'
import {StatusPageHeader} from '../containers/StatusPageHeader'
import {useHierarchyData} from '../hooks/useHierarchy'
import {PlantEquipmentDataProvider} from '../hooks/usePlantsEquipmentStatus'
import {useStatusInitialTimeRange} from '../hooks/useStatusInitialTimeRange'
import {useTimeRange} from '../hooks/useTimeRange'
import {useTranslationPrefix} from '../hooks/useTranslationPrefix'
import {HierarchyNode} from '../types'

export const KilnMillStatus: FC = () => {
  const {t} = useTranslation()
  const {performancePrefix} = useTranslationPrefix()
  const {from, to} = useTimeRange()

  const timeRangeLoaded = useStatusInitialTimeRange('status')

  const {
    data: equipmentStatusData = {},
    refetch: equipmentStatusRefetch,
    isInitialLoading: equipmentStatusIsInitialLoading,
    isError: isEquipmentStatusError,
    isRefetching: isEquipmentStatusRefetching
  } = useEquipmentStatus(
    {
      startDate: from.toJSON(),
      endDate: to.toJSON()
    },
    HierarchyNodeType.Global,
    {enabled: timeRangeLoaded}
  )

  const hierarchy = useHierarchyData()

  const areaList: string[] = useMemo(
    () =>
      hierarchy?.children.reduce((acc: string[], hierarchyNode: HierarchyNode) => {
        if (hierarchyNode.isAllowed) acc.push(hierarchyNode.id)
        return acc
      }, []) || [],
    [hierarchy]
  )

  const [expandedAreas, toggleExpandedAreas] = useExpandedGroups(
    areaList,
    'kiln-mill-area-expanded'
  )

  React.useEffect(() => {
    if (!equipmentStatusIsInitialLoading && expandedAreas.length !== areaList?.length)
      toggleExpandedAreas('all')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentStatusIsInitialLoading])

  if (!timeRangeLoaded) {
    return <Fragment />
  }

  return (
    <PageContainer>
      <StatusPageHeader
        title={t(`${performancePrefix}.kilnStatus.kilnAndMillPageTitle`)}
        filterComponent={
          <>
            <DateTimeRangePicker type="status" disableCustomSelect />
            <ExpandCollapseButton
              sx={mergeSxs(buttonOnBlueSx, {
                maxHeight: 58,
                width: {xs: '100%', sm: '150px'}
              })}
              collapseLabel={t(`${performancePrefix}.button.collapseAll`)}
              expandLabel={t(`${performancePrefix}.button.expandAll`)}
              expanded={expandedAreas.length === areaList?.length}
              onClick={() => toggleExpandedAreas('all')}
              data-test-id="chevron-open-all-button"
            />
          </>
        }
      />

      <DataContentWrapper
        showLoader={equipmentStatusIsInitialLoading}
        data={hierarchy?.children}
        retryFunction={() => {
          if (isEquipmentStatusError) void equipmentStatusRefetch()
        }}
        isError={isEquipmentStatusError}
        isRefetching={isEquipmentStatusRefetching}
      >
        <PlantEquipmentDataProvider plantEquipmentStatuses={equipmentStatusData}>
          {hierarchy?.children?.map((hierarchyNode, index) => (
            <CollapseLocationItem
              key={`kiln-status-collapse-item-${index}`}
              handleOpen={toggleExpandedAreas}
              isOpen={expandedAreas.includes(hierarchyNode.id)}
              index={index}
              location={hierarchyNode}
            />
          ))}
        </PlantEquipmentDataProvider>
      </DataContentWrapper>
    </PageContainer>
  )
}
